// Sound Manager using Web Audio API with effects

const MUTE_STORAGE_KEY = 'simulator_muted';

class SoundManager {
  constructor() {
    this.context = null;
    this.sounds = {};
    this.initialized = false;
    this.muted = false;
    
    // Restore mute state from localStorage
    try {
      const saved = localStorage.getItem(MUTE_STORAGE_KEY);
      if (saved === 'true') {
        this.muted = true;
      }
    } catch (e) {}
  }
  
  // Get mute state
  isMuted() {
    return this.muted;
  }
  
  // Set mute state
  setMuted(muted) {
    this.muted = muted;
    try {
      localStorage.setItem(MUTE_STORAGE_KEY, muted ? 'true' : 'false');
    } catch (e) {}
  }
  
  // Toggle mute state
  toggleMute() {
    this.setMuted(!this.muted);
    return this.muted;
  }

  async init() {
    if (this.initialized) return;
    
    console.log('Initializing sound manager...');
    
    try {
      this.context = new (window.AudioContext || window.webkitAudioContext)();
      console.log('AudioContext created, state:', this.context.state);
      
      // Load all sounds
      const soundFiles = {
        dragin: '/sounds/smelting_dragin.wav',
        loading: '/sounds/smelting_loding.wav',
        finalA: '/sounds/smelting_finalA.wav',
        finalB: '/sounds/smelting_finalB.wav',
        finalC: '/sounds/smelting_finalC.wav',
        finalD: '/sounds/smelting_finalD.wav',
      };
      
      const loadPromises = Object.entries(soundFiles).map(async ([key, path]) => {
        const response = await fetch(path);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await this.context.decodeAudioData(arrayBuffer);
        this.sounds[key] = audioBuffer;
        console.log(`Loaded sound: ${key}`);
      });
      
      await Promise.all(loadPromises);
      this.initialized = true;
      console.log('All sounds loaded successfully!');
    } catch (error) {
      console.error('Failed to initialize sound manager:', error);
    }
  }

  // Resume context if needed (for browsers that require user interaction)
  async resume() {
    if (this.context && this.context.state === 'suspended') {
      await this.context.resume();
    }
  }
  
  // Alias for resume (for iOS/Safari background handling)
  async resumeAudio() {
    return this.resume();
  }

  // Create a reverb effect
  createReverb(duration = 2, decay = 2) {
    const rate = this.context.sampleRate;
    const length = rate * duration;
    const impulse = this.context.createBuffer(2, length, rate);
    const impulseL = impulse.getChannelData(0);
    const impulseR = impulse.getChannelData(1);

    for (let i = 0; i < length; i++) {
      const n = length - i;
      impulseL[i] = (Math.random() * 2 - 1) * Math.pow(n / length, decay);
      impulseR[i] = (Math.random() * 2 - 1) * Math.pow(n / length, decay);
    }

    const convolver = this.context.createConvolver();
    convolver.buffer = impulse;
    return convolver;
  }

  // Play sound with effects
  playSound(soundKey, options = {}) {
    // Skip if muted
    if (this.muted) return;
    
    if (!this.initialized || !this.sounds[soundKey]) return;

    const {
      pitch = 1.0,          // Playback rate (pitch shift)
      volume = 1.0,         // Volume (0-1)
      reverb = 0,           // Reverb amount (0-1)
      filter = null,        // { type: 'lowpass'/'highpass', frequency: 1000 }
      delay = 0,            // Delay before playing (seconds)
      pan = 0               // Stereo pan (-1 to 1)
    } = options;

    const source = this.context.createBufferSource();
    source.buffer = this.sounds[soundKey];
    source.playbackRate.value = pitch;

    // Create gain node for volume
    const gainNode = this.context.createGain();
    gainNode.gain.value = volume;

    // Create panner for stereo positioning
    const panNode = this.context.createStereoPanner();
    panNode.pan.value = pan;

    // Chain: source -> effects -> destination
    let currentNode = source;

    // Add filter if specified
    if (filter) {
      const filterNode = this.context.createBiquadFilter();
      filterNode.type = filter.type;
      filterNode.frequency.value = filter.frequency;
      currentNode.connect(filterNode);
      currentNode = filterNode;
    }

    // Add reverb if specified
    if (reverb > 0) {
      const dryGain = this.context.createGain();
      const wetGain = this.context.createGain();
      const reverbNode = this.createReverb(1.5, 3);

      dryGain.gain.value = 1 - reverb;
      wetGain.gain.value = reverb;

      currentNode.connect(dryGain);
      currentNode.connect(reverbNode);
      reverbNode.connect(wetGain);

      dryGain.connect(panNode);
      wetGain.connect(panNode);
    } else {
      currentNode.connect(panNode);
    }

    panNode.connect(gainNode);
    gainNode.connect(this.context.destination);

    source.start(this.context.currentTime + delay);
  }

  // Play place stone sound
  async playPlaceStone() {
    if (this.muted) return;
    await this.resume();
    this.playSound('dragin', {
      pitch: 0.95 + Math.random() * 0.1, // Slight pitch variation
      volume: 0.8,
      reverb: 0.15
    });
  }

  // Play loading/augmenting sound
  async playLoading() {
    if (this.muted) return;
    await this.resume();
    this.playSound('loading', {
      pitch: 0.98 + Math.random() * 0.04,
      volume: 0.9,
      reverb: 0.2
    });
  }

  // Play result sound based on outcome
  async playResult(outcomeType, rarity) {
    if (this.muted) return;
    await this.resume();
    
    let soundKey;
    let pitch = 1.0;
    let reverb = 0.15;
    let filter = null;

    if (outcomeType == 'generic') {
      if (Math.random() < 0.5) {
        outcomeType = 'generic_a';
      } else {
        outcomeType = 'generic_b';
      }
    }

    switch (outcomeType) {
      case 'skill':
        soundKey = 'finalD';
        pitch = 1.0 + Math.random() * 0.05; // Slightly higher pitch
        reverb = 0.35; // More reverb for epic feel
        filter = { type: 'highpass', frequency: 200 }; // Brighter sound
        break;
      
      case 'core_stat':
        soundKey = 'finalC';
        pitch = 0.98 + Math.random() * 0.04;
        reverb = 0.25;
        filter = { type: 'highpass', frequency: 150 };
        break;
      
      case 'generic_a':
        soundKey = 'finalA';
        pitch = 0.95 + Math.random() * 0.05;
        reverb = 0.15;
        break;
      
      case 'generic_b':
        soundKey = 'finalB';
        pitch = 0.95 + Math.random() * 0.05;
        reverb = 0.15;
        break;
      
      default:
        soundKey = 'finalA';
    }

    // Add extra flair for legendary outcomes
    if (rarity === 'legendary') {
      // Play a subtle pre-sound
      this.playSound(soundKey, {
        pitch: pitch * 0.7,
        volume: 0.3,
        reverb: 0.5,
        delay: 0
      });
      
      // Main sound with slight delay
      this.playSound(soundKey, {
        pitch,
        volume: 1.0,
        reverb,
        filter,
        delay: 0.15
      });
      
      // Add a high-pitched "sparkle"
      this.playSound(soundKey, {
        pitch: pitch * 1.5,
        volume: 0.4,
        reverb: 0.6,
        filter: { type: 'highpass', frequency: 800 },
        delay: 0.3
      });
    } else {
      // Normal playback for common/rare
      this.playSound(soundKey, {
        pitch,
        volume: 0.9,
        reverb,
        filter
      });
    }
  }
}

// Export singleton instance
export const soundManager = new SoundManager();
